import type { ProjectMeta } from './types';

export const projects = [
  {
    slug: 'timetogo',
    title: 'TimeToGo',
    eyebrow: 'iOS + macOS',
    hook: 'Traffic at a glance—right in your Mac menu bar.',
    summary: 'Stop checking traffic. Keep your focus.',
    platforms: ['ios', 'macos'],
    status: 'live',
    links: [
      { kind: 'appstore-ios', url: 'https://apps.apple.com/app/id6758226358' },
      { kind: 'appstore-mac', url: 'https://apps.apple.com/app/id6758940999?pt=126701792&ct=ai4u-portfolio&mt=8' },
    ],
    outcomes: [
      { value: '#4', label: 'NZ Navigation', caption: 'Observed in the Mac App Store, July 2026.' },
      { value: '#9', label: 'US Navigation', caption: 'Observed in the Mac App Store, July 2026.' },
      { value: '17K', label: 'Reddit views', caption: 'Rounded display on the r/macapps launch post.' },
      { value: '49', label: 'Reddit upvotes', caption: 'Snapshot from the r/macapps launch post.' },
    ],
    cardMedia: { type: 'gif', src: '/media/timetogo/app-preview-magnifier-v3-6s-loop.gif', alt: 'TimeToGo App Preview showing live traffic changes through a magnified Mac menu bar', aspectRatio: '16 / 10' },
    appIcon: '/media/timetogo/app-icon-mac-v1.png',
    accentColor: '#6366f1',
    order: 1,
  },
  {
    slug: 'echo',
    title: 'Echo',
    eyebrow: 'Interactive AI narrative',
    hook: 'Answer a signal from Mars.',
    summary: 'An interactive sci-fi story told through a live communication link with Lyra Voss, an astronaut stranded on Mars.',
    platforms: ['ios'],
    status: 'live',
    statusLabel: 'Live on the App Store',
    links: [
      { kind: 'appstore-ios', url: 'https://apps.apple.com/us/app/echo-a-signal-from-mars/id6762421185' },
      { kind: 'web', url: '/echo/support/', label: 'Echo support' },
    ],
    cardMedia: { type: 'image', src: '/media/echo/app-icon-astronaut-v1.png', alt: 'Echo app icon showing an astronaut on Mars', aspectRatio: '16 / 10', presentation: 'icon' },
    appIcon: '/media/echo/app-icon-astronaut-v1.png',
    accentColor: '#38bdf8',
    order: 2,
  },
  {
    slug: 'toki',
    title: 'Toki',
    eyebrow: 'Time intelligence',
    hook: 'Track your time. Understand your day.',
    summary: 'A time-tracking app for recording daily activity and exploring sleep and focus patterns across iOS and the web.',
    platforms: ['web', 'ios'],
    status: 'in-development',
    statusLabel: 'In Beta Testing',
    links: [{ kind: 'web', url: '/toki', label: 'Open Toki' }],
    cardMedia: { type: 'image', src: '/media/toki/card-placeholder.svg', alt: 'Toki project placeholder artwork', aspectRatio: '16 / 10' },
    appIcon: '/media/toki/app-icon-placeholder-v1.svg',
    accentColor: '#f43f5e',
    order: 3,
  },
  {
    slug: 'aitourism',
    title: 'AITourism',
    eyebrow: 'AI tourism training',
    hook: 'Train overseas sales teams on New Zealand tourism—with AI by their side.',
    summary: 'A multilingual B2B learning platform combining focused product courses with AI Q&A grounded in operator documents.',
    platforms: ['web'],
    status: 'live',
    statusLabel: 'Live Demo',
    links: [{ kind: 'web', url: 'https://aitourism.ai4u.co.nz/', label: 'Open live demo' }],
    cardMedia: { type: 'image', src: '/media/aitourism/home-v1.png', alt: 'AITourism homepage for New Zealand tourism sales training', aspectRatio: '16 / 10' },
    accentColor: '#0f766e',
    order: 4,
  },
  {
    slug: 'wechat-video-workflow',
    title: 'WeChat AI Video',
    eyebrow: 'Creative workflow',
    hook: 'Turn chat-shaped stories into editable AI video workflows.',
    summary: 'A local production system for composing WeChat conversations and reusable video jobs.',
    platforms: ['web', 'wechat', 'workflow'],
    status: 'case-study',
    links: [{ kind: 'web', url: '/wechat-video-workflow/', label: 'Open workflow demo' }],
    outcomes: [
      { value: '1', label: 'Published sample', caption: 'The first 9:16 prototype was user-approved and published to WeChat Channels.' },
      { value: '2', label: 'Reusable templates', caption: 'The workflow supports a main-chat template and an optional background-story ending.' },
    ],
    cardMedia: { type: 'image', src: '/media/wechat-video-workflow/card-marketing-story-v1.webp', alt: 'WeChat conversation from a completed vertical marketing video', aspectRatio: '16 / 10', presentation: 'portrait' },
    accentColor: '#22c55e',
    order: 5,
  },
  {
    slug: 'fashion-videogen',
    title: 'Fashion VideoGen',
    eyebrow: 'Generative video',
    hook: 'From a fashion image to motion-ready campaign experiments.',
    summary: 'A repeatable generative pipeline for testing visual concepts and vertical video output.',
    platforms: ['workflow'],
    status: 'case-study',
    links: [{ kind: 'web', url: '/projects/fashion-videogen', label: 'View case study' }],
    cardMedia: { type: 'image', src: '/media/fashion-videogen/card-placeholder.svg', alt: 'Fashion VideoGen placeholder artwork', aspectRatio: '16 / 10' },
    accentColor: '#ec4899',
    order: 6,
  },
  {
    slug: 'shadowing',
    title: 'Shadowing',
    eyebrow: 'English learning · Engineering journal',
    hook: 'From transcription experiments to a place to listen, shadow, and speak.',
    summary: 'The engineering story behind an English learning platform: model comparisons, bilingual lessons, production tracking, and per-learner course access.',
    platforms: ['web'],
    status: 'live',
    statusLabel: 'Live · Engineering journal',
    links: [{ kind: 'web', url: '/helloworld/shadowing/', label: 'Open Shadowing' }],
    cardMedia: { type: 'image', src: '/media/shadowing/library-20260915-v1.png', alt: 'Shadowing course library with search, video lessons and availability indicators', aspectRatio: '16 / 10' },
    order: 6.5,
  },
  {
    slug: 'ai4u',
    title: 'AI4U',
    eyebrow: 'Portfolio platform',
    hook: 'The home for AI products that make it all the way to launch.',
    summary: 'The evolving portfolio and distribution surface for GENAI Technologies.',
    platforms: ['web'],
    status: 'live',
    links: [{ kind: 'web', url: 'https://ai4u.co.nz/', label: 'Visit AI4U' }],
    cardMedia: { type: 'image', src: '/media/ai4u/card-placeholder.svg', alt: 'AI4U project placeholder artwork', aspectRatio: '16 / 10' },
    accentColor: '#a78bfa',
    order: 7,
  },
] satisfies readonly ProjectMeta[];

const projectsBySlug = new Map(projects.map((project) => [project.slug, project]));

export const getProject = (slug: string): ProjectMeta | undefined => projectsBySlug.get(slug);
